'use client'

import Link from 'next/link'
import clsx from 'clsx'
import { FiChevronRight } from 'react-icons/fi'
import { useLanguage } from '@/app/(frontend)/providers/LanguageProvider'

type BreadcrumbItem = {
  href: string
  name: string
}

export default function Breadcrumb({ breadcrumbs }: { breadcrumbs: BreadcrumbItem[] }) {
  const { language } = useLanguage()
  const isArabic = language === 'ar'

  const homeLabel = isArabic ? 'الرئيسية' : 'Home'
  const fullBreadcrumbs: BreadcrumbItem[] = [{ href: '/', name: homeLabel }, ...breadcrumbs]

  // Only show last 2 items in mobile
  const lastTwo = fullBreadcrumbs.slice(-2)

  return (
    <section className="w-full pt-3 md:pt-5 lg:pt-6 xl:pt-6 2xl:pt-[60px] mb-[-2px] bg-white text-black flex justify-center">
      <div className="section-container mx-auto">
        {/* Full breadcrumb - visible on md+ */}
        <nav
          dir={isArabic ? 'rtl' : 'ltr'}
          className={clsx('hidden md:flex text-sm text-gray-400 items-center gap-1 justify-start')}
        >
          {fullBreadcrumbs.map((item, index) => {
            const isLast = index === fullBreadcrumbs.length - 1
            const label = item.name

            return (
              <span key={item.href} className="flex items-center gap-[8px]">
                {index > 0 && (
                  <FiChevronRight
                    className={clsx(
                      'text-sm xl:text-[16px] text-[#ee3e25]',
                      isArabic && 'rotate-180',
                    )}
                  />
                )}
                {isLast ? (
                  <span className="text-[#ee3e25] text-sm xl:text-[15px] 2xl:text[17px]">{label}</span>
                ) : (
                  <Link
                    href={item.href}
                    className="transition-colors text-sm xl:text-[15px] 2xl:text-[16px]  font-[400] hover:underline"
                  >
                    {label}
                  </Link>
                )}
              </span>
            )
          })}
        </nav>

        {/* Mobile breadcrumb - only last 2 items */}
        <nav
          dir={isArabic ? 'rtl' : 'ltr'}
          className={clsx(
            'flex md:hidden text-sm text-gray-400 items-center gap-1 justify-start overflow-x-auto no-scrollbar scroll-smooth',
          )}
        >
          {lastTwo.map((item, index) => {
            const isLast = index === lastTwo.length - 1
            const label = item.name

            return (
              <span key={item.href} className="flex items-center gap-[8px]">
                {index > 0 && (
                  <FiChevronRight
                    className={clsx(
                      'text-sm xl:text-[16px] text-[#ee3e25]',
                      isArabic && 'rotate-180',
                    )}
                  />
                )}
                {isLast ? (
                  <span className="text-[#ee3e25] text-sm xl:text-[15px] ">{label}</span>
                ) : (
                  <Link
                    href={item.href}
                    className="transition-colors text-sm xl:text-[15px]   font-[400] hover:underline"
                  >
                    {label}
                  </Link>
                )}
              </span>
            )
          })}
        </nav>
      </div>
    </section>
  )
}
